import { useEffect } from 'react';

/**
 * Per-route <head> management without a helmet dependency. Every tag this
 * hook adds carries `data-head="route"`, and the build-time prerender marks
 * its tags the same way, so a page mounting on top of a static snapshot
 * replaces them instead of duplicating them.
 */

export interface HeadSpec {
  title: string;
  description: string;
  canonical?: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  jsonLd?: object | object[];
  /** article:published_time etc. */
  extra?: Array<{ property?: string; name?: string; content: string }>;
}

const SITE_TITLE = 'EasyToCook | Recipe Keeper, Meal Planner & Macro Tracker';
const SITE_DESCRIPTION =
  'Save recipes from Instagram, TikTok, YouTube and any website. EasyToCook turns them into ingredients, steps and macros, plans your week, writes the shopping list and logs what you ate. Free on iPhone.';
const SITE_OG_TITLE = 'EasyToCook | Save any recipe with just a tap';
const SITE_OG_DESCRIPTION =
  'Recipe keeper, meal planner and macro tracker in one. Save from Instagram, TikTok, YouTube or any website. Free on iPhone.';
const SITE_OG_IMAGE = 'https://easytocook.com.au/images/og.png';

const MARK = 'data-head';

const setMeta = (attr: 'name' | 'property', key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.setAttribute(MARK, 'route');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const removeManaged = () => {
  document.head.querySelectorAll(`[${MARK}="route"]`).forEach((el) => el.remove());
};

export const applyHead = (spec: HeadSpec) => {
  removeManaged();
  document.title = spec.title;
  setMeta('name', 'description', spec.description);
  setMeta('property', 'og:title', spec.title);
  setMeta('property', 'og:description', spec.description);
  setMeta('property', 'og:type', spec.ogType ?? 'website');
  setMeta('name', 'twitter:title', spec.title);
  setMeta('name', 'twitter:description', spec.description);
  if (spec.ogImage) {
    setMeta('property', 'og:image', spec.ogImage);
    setMeta('name', 'twitter:image', spec.ogImage);
  }
  if (spec.canonical) {
    const link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', spec.canonical);
    link.setAttribute(MARK, 'route');
    document.head.appendChild(link);
    setMeta('property', 'og:url', spec.canonical);
  }
  spec.extra?.forEach((m) => {
    const el = document.createElement('meta');
    if (m.property) el.setAttribute('property', m.property);
    if (m.name) el.setAttribute('name', m.name);
    el.setAttribute('content', m.content);
    el.setAttribute(MARK, 'route');
    document.head.appendChild(el);
  });
  if (spec.jsonLd) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(MARK, 'route');
    script.text = JSON.stringify(spec.jsonLd);
    document.head.appendChild(script);
  }
};

export const resetHead = () => {
  removeManaged();
  document.title = SITE_TITLE;
  setMeta('name', 'description', SITE_DESCRIPTION);
  setMeta('property', 'og:title', SITE_OG_TITLE);
  setMeta('property', 'og:description', SITE_OG_DESCRIPTION);
  setMeta('property', 'og:type', 'website');
  setMeta('property', 'og:image', SITE_OG_IMAGE);
  setMeta('name', 'twitter:title', SITE_OG_TITLE);
  setMeta('name', 'twitter:description', SITE_OG_DESCRIPTION);
  setMeta('name', 'twitter:image', SITE_OG_IMAGE);
  document.head.querySelector('meta[property="og:url"]')?.remove();
};

export const useHead = (spec: HeadSpec | null) => {
  const key = spec ? JSON.stringify(spec) : null;
  useEffect(() => {
    if (!spec) return;
    applyHead(spec);
    return resetHead;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
};
